import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { bp } from 'Styles/helpers';
import objectPath from 'object-path';
import { rem, rgba } from 'polished';
import screen from 'superior-mq';
import truncate from 'Lib/truncate';
import { Link } from '../../routes';
import RatioImg from '../ratio-img';

const ProgramCard = ({ program, parentRoute, colorConfig }) => {
  if (!program || (program && !program.fields)) return null;

  const fallbackImage = `${process.env.S3_ROOT}/w4w-static/images/project-fallback-dark.svg`;
  const imageParams = '?w=322&h=224&q=70&fm=jpg&fl=progressive&fit=thumb';

  const formattedGoal = (program.fields.goal && program.fields.goal.formatted) || '$0';
  const formattedFunds = (program.fundsRaised && program.fundsRaised.formatted) || '$0';
  const rawFunds = (program.fundsRaised && program.fundsRaised.raw) || 0;
  const rawGoal = (program.fields.goal && program.fields.goal.raw) || 0;

  const imagePath = objectPath.get(program.fields, 'featuredImage.photo.file.url');
  const programImage = imagePath
    ? `${imagePath}${imageParams}`
    : fallbackImage;

  return (
    <Link route={`${parentRoute}/${program.slug}`}>
      <CardLink>

        <div>
          <RatioImg
            src={programImage}
            width={238}
            height={165}
            alt={`${program.fields.title} featured image`}
          />

          {program.fields && (
            <Fragment>
              <SecondaryText colorConfig={colorConfig}>{program.fields.location}</SecondaryText>
              <ProgramTitle colorConfig={colorConfig}>
                {truncate(program.fields.title, 50)}
              </ProgramTitle>
            </Fragment>
          )}
        </div>

        <div>
          <DonationStatus colorConfig={colorConfig}>
            {formattedFunds} / {formattedGoal} <Currency>{program.settlementCurrency}</Currency>
          </DonationStatus>

          <ProgressBar>
            <ProgressBar.Status progress={Math.round((rawFunds / rawGoal) * 100) / 100} />
          </ProgressBar>
        </div>

      </CardLink>
    </Link>
  );
};

const CardLink = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  position: relative;
  text-decoration: none;
`;

const Currency = styled.em`
  font-size: .75rem;
  font-style: normal;
  position: relative;
`;

const SecondaryText = styled.span`
  display: block;
  margin: 30px auto 8px;
  font-size: ${rem(14)};
  color: ${props => props.colorConfig === 'courier' ? rgba(props.theme.blue, 0.5) : props.theme.blue};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${screen.below(bp.mobile, `
    margin: 15px auto 8px;
    font-size: ${rem(11)};
  `)}
`;

const ProgramTitle = styled.h6`
  font-family: ${props => props.theme.sansFont};
  font-size: ${rem(22)};
  line-height: 1.2;
  color: ${props => props.colorConfig === 'courier' ? '#fff' : props.theme.black};

  ${screen.below(bp.mobile, `
    font-size: ${rem(15)};
  `)}
`;

const DonationStatus = styled(SecondaryText)`
  color: ${props => props.colorConfig === 'courier' ? rgba(props.theme.blue, 0.5) : props.theme.darkBlue};
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  margin-top: 12px;
  background-color: ${props => props.colorConfig === 'courier' ? rgba(props.theme.blue, 0.5) : props.theme.lightBlue};
`;

ProgressBar.Status = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.darkBlue};
  transform: scaleX(${props => (props.progress > 1 ? 1 : props.progress)});
  transform-origin: left center;
`;

ProgramCard.propTypes = {
  program: PropTypes.object,
  parentRoute: PropTypes.string,
  colorConfig: PropTypes.string,
};

export default ProgramCard;
